/**
 * SidebarMobile Component
 *
 * Mobile drawer implementation with:
 * - Slide-in animation from left
 * - Backdrop overlay with click-to-close
 * - Full navigation functionality
 * - Search bar
 * - Theme toggle
 * - Auto-close after navigation
 * - Escape key support
 * - Body scroll lock when open
 *
 * @module SidebarMobile
 */

import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { IconX } from '@tabler/icons-react';
import { useSidebar } from '../hooks/useSidebar';
import SearchBar from './SearchBar';
import NavigationItem from './NavigationItem';
import ThemeToggle from './ThemeToggle';

/**
 * SidebarMobile - Mobile drawer
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Is drawer open
 * @param {Function} props.onClose - Close handler
 */
const SidebarMobile = ({ isOpen, onClose }) => {
  const drawerRef = useRef(null);
  const previouslyFocusedRef = useRef(null);
  const {
    navigationAreas,
    navigationUtilities,
    activeNavigationArea,
    activeMenu,
    handleMenuClick
  } = useSidebar();

  // Keep keyboard and assistive-technology interaction inside the open drawer.
  useEffect(() => {
    if (!isOpen) return undefined;

    previouslyFocusedRef.current = document.activeElement;
    const backgroundElements = [
      document.querySelector('main'),
      document.querySelector('aside[aria-label="Mobile navigation"]')
    ].filter(Boolean);
    const previousBackgroundState = backgroundElements.map(element => ({
      element,
      ariaHidden: element.getAttribute('aria-hidden'),
      inert: element.inert
    }));

    backgroundElements.forEach(element => {
      element.inert = true;
      element.setAttribute('aria-hidden', 'true');
    });

    const focusableSelector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    const focusFirstControl = () => {
      drawerRef.current?.querySelector(focusableSelector)?.focus();
    };
    const focusFrame = window.requestAnimationFrame(focusFirstControl);

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab' || !drawerRef.current) return;
      const focusable = Array.from(drawerRef.current.querySelectorAll(focusableSelector));
      if (focusable.length === 0) {
        event.preventDefault();
        drawerRef.current.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previousBackgroundState.forEach(({ element, ariaHidden, inert }) => {
        element.inert = inert;
        if (ariaHidden === null) element.removeAttribute('aria-hidden');
        else element.setAttribute('aria-hidden', ariaHidden);
      });
      if (previouslyFocusedRef.current?.isConnected) previouslyFocusedRef.current.focus();
    };
  }, [isOpen, onClose]);

  // Don't render anything if not open (for performance)
  if (!isOpen) return null;

  const handleNavigationClick = (menuId, path) => {
    // Call the original menu click handler
    handleMenuClick(menuId, path);
    // Close drawer after navigation
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="ui-motion-enter fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <aside
        ref={drawerRef}
        id="mobile-sidebar-drawer"
        data-side="left"
        className={`
          ui-motion-panel fixed top-0 left-0 h-full w-[280px] sm:w-[320px] max-w-[80vw]
          bg-surface-0
          z-50 flex flex-col
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        aria-label="Mobile navigation drawer"
        aria-modal="true"
        role="dialog"
        tabIndex={-1}
      >
        {/* Header with Close Button */}
        <div className="flex items-center justify-between p-4 border-b border-glass-border">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-accent flex items-center justify-center">
              <span className="text-accent-foreground font-black text-lg font-display">T</span>
            </div>
            <div>
              <h2 className="font-display uppercase font-black text-lg text-text-primary tracking-tight">
                TAKEOVER
              </h2>
              <p className="text-xs text-text-tertiary">
                Creator Platform
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-glass-bg-hover min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close menu"
          >
            <IconX size={20} className="text-text-secondary" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="p-4 border-b border-glass-border">
          <SearchBar />
        </div>

        {/* Task areas - secondary destinations appear in the page context bar. */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-1" aria-label="Primary navigation">
          <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">
            Areas
          </p>
          {navigationAreas.length === 0 ? (
            <div className="text-center text-text-tertiary py-8">
              <p className="text-sm">No menu items found</p>
            </div>
          ) : (
            navigationAreas.map(area => (
              <NavigationItem
                key={area.id}
                item={area}
                isActive={activeNavigationArea?.id === area.id}
                onClick={() => handleNavigationClick(area.id, area.path)}
              />
            ))
          )}
        </nav>

        {navigationUtilities.length > 0 && (
          <div className="px-4 py-3 border-t border-glass-border space-y-1">
            <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">
              Utilities
            </p>
            {navigationUtilities.map(item => (
              <NavigationItem
                key={item.id}
                item={item}
                isActive={activeMenu === item.id}
                onClick={() => handleNavigationClick(item.id, item.path)}
              />
            ))}
          </div>
        )}

        <div className="p-4 border-t border-glass-border">
          <ThemeToggle />
        </div>
      </aside>
    </>
  );
};

SidebarMobile.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default React.memo(SidebarMobile);
